<template>
  <v-container class="px-4 py-4" fluid>
    <v-row dense>
      <v-col cols="3">
        <TextFieldRef :label="$t('ELE0134')" :value="manufacturerCompanyName" />
      </v-col>
      <v-col cols="3">
        <TextFieldRef :label="$t('ELE0031')" :value="partCd" />
      </v-col>
      <v-col cols="3">
        <TextFieldRef :label="$t('ELE0049')" :value="lotNo" />
      </v-col>
      <v-col cols="3">
        <TextFieldRef :label="$t('ELE0050')" :value="lotQty" />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="6">
        <InspLotSerialNoInput
          v-model="barcode"
          :disableFocus="confirmDialog.flag"
          @scanned="onAddItem"
          ref="txtBarcode"
        />
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-card-title>
          {{ $t('ELE0549') }}
          <div class="pl-3">
            {{ this.items.length }}
          </div>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="items"
          :item-class="setRowClass"
          :items-per-page="-1"
          hide-default-footer
          dense
          height="290"
          fixed-header
        >
          <template v-slot:[`item.action`]="{ index }">
            <IconDelete :disabled="!canDelete" @click="onDeleteItem(index)" />
          </template>
          <template v-slot:[`item.error`]="{ item }">
            <div style="white-space: pre">{{ item.error }}</div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-divider />
    <v-card-actions>
      <v-row dense>
        <v-col>
          <span class="d-flex justify-end">
            <BtnSave :disabled="!canSave" @click="onClickSave" />
          </span>
        </v-col>
      </v-row>
    </v-card-actions>
    <ConfirmDialog
      :dialog.sync="confirmDialog.flag"
      :confirmWord="confirmDialog.confirmWord"
      :btnLabel="confirmDialog.btnLabel"
      btnColor="error"
      @cancel="cancel"
      @proceed="proceed"
    />
  </v-container>
</template>
<script>
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import CheckUtil from '@/mixin/check-utils.js'

import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import InspLotSerialNoInput from '@/components/atoms/text-field/InspLotSerialNoInput'
import BtnSave from '@/components/atoms/button/BtnSave'
import IconDelete from '@/components/atoms/icon/IconDelete.vue'
import ConfirmDialog from '@/components/organisms/common/dialog/ConfirmDialog'

export default {
  name: 'ImpSerialNoForm',

  mixins: [APIUtils, Message, Loading, CheckUtil],

  components: {
    TextFieldRef,
    InspLotSerialNoInput,
    BtnSave,
    IconDelete,
    ConfirmDialog,
  },

  props: {
    manufacturerCompanyCd: {
      type: String,
      required: true,
    },
    manufacturerCompanyName: {
      type: String,
      required: true,
    },
    inspCompanyCd: {
      type: String,
      required: true,
    },
    inspCompanyCatCd: {
      type: String,
      required: true,
    },
    lotNo: {
      type: String,
      required: true,
    },
    partCd: {
      type: String,
      required: true,
    },
    inspTimingCd: {
      type: String,
      required: true,
    },
    lotQty: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      barcode: '',
      errorLineCount: '',
      items: [],
      headers: [
        { text: '', width: '90', align: 'center', divider: true, sortable: false, value: 'action' },
        { text: this.$t('ELE0548'), sortable: false, value: 'serialNo', divider: true },
        { text: this.$t('ELE0550'), sortable: false, value: 'registeredBy', divider: true },
        { text: this.$t('ELE0100'), sortable: false, value: 'error' },
      ],
      confirmDialog: {
        flag: false,
        confirmWord: '',
        btnLabel: '',
      },
      deleteTarget: { seq: null, serialNo: '', versionNo: null },
    }
  },

  computed: {
    canDelete: {
      get() {
        return this.isInspectableCompany(this.inspCompanyCd, this.inspCompanyCatCd)
      },
    },
    canSave: {
      get() {
        return this.canDelete && this.items.some((item) => item.seq == null)
      },
    },
  },

  mounted() {
    this.onClickSearch()
  },

  methods: {
    async onClickSearch() {
      this.startLoading()
      try {
        const params = {
          inspTimingCd: this.inspTimingCd,
          manufacturerCompanyCd: this.manufacturerCompanyCd,
          partCd: this.partCd,
          lotNo: this.lotNo,
          inspCompanyCd: this.inspCompanyCd,
        }
        this.items = []
        const elements = await API.get('/box/serialNos', params)
        this.items = elements.map((element, index) => ({
          ...element,
          registeredBy: `${element.itemRegisteredBy}: ${element.registeredByName}`,
          originalIndex: index,
        }))
      } catch (error) {
        this.items = []
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0548') })))
      } finally {
        this.stopLoading()
      }
    },

    async onClickSave() {
      this.startLoading()
      try {
        //エラーメッセージを初期化しoriginalIndexを採番
        this.items.forEach((item, index) => {
          item.error = ''
          item.originalIndex = index
        })
        //新規追加分のみ抽出
        const items = this.items
          .filter((item) => item.seq == null)
          .map((item) => ({
            serialNo: item.serialNo,
            originalIndex: item.originalIndex,
          }))

        let body = {
          items: items,
          inspTimingCd: this.inspTimingCd,
          manufacturerCompanyCd: this.manufacturerCompanyCd,
          partCd: this.partCd,
          lotNo: this.lotNo,
          inspCompanyCd: this.inspCompanyCd,
        }
        const elements = await API.post('/box/serialNos/check', body)
        if (elements.isResult) {
          const serialNos = items.map((item) => item.serialNo)
          await this.registerSerial(serialNos)
        } else {
          this.errorLineCount = elements.errorLineCount
          elements.errorMessages.forEach((element) => {
            this.items[element.lineNo].error = element.bodies.join('\r\n')
          })
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0548') })))
      } finally {
        this.$refs.txtBarcode.focus()
        this.stopLoading()
      }
    },
    async registerSerial(serialNos) {
      try {
        let body = {
          serialNos: serialNos,
          inspTimingCd: this.inspTimingCd,
          manufacturerCompanyCd: this.manufacturerCompanyCd,
          partCd: this.partCd,
          lotNo: this.lotNo,
          inspCompanyCd: this.inspCompanyCd,
        }
        await API.post('/box/serialNos/register', body)
        this.setSuccessMessage(this.$t('MSG0011', { p0: this.$t('ELE0548') }))
        this.onClickSearch()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0548') })))
      }
    },
    onDeleteItem(index) {
      const seq = this.items[index].seq
      //seqがあればDBに既に保存されている値なので確認ダイアログ表示
      if (seq) {
        this.deleteTarget = {
          seq: seq,
          serialNo: this.items[index].serialNo,
          versionNo: this.items[index].versionNo,
        }
        this.confirmDialog.confirmWord = this.$t('MSG0013')
        this.confirmDialog.btnLabel = this.$t('ELE0036')
        this.confirmDialog.flag = true
      } else {
        this.items.splice(index, 1)
      }
    },
    cancel() {
      this.deleteTarget = { seq: null, serialNo: '', versionNo: null }
      this.confirmDialog.flag = false
    },
    async proceed() {
      this.startLoading()
      try {
        this.confirmDialog.flag = false
        const url = '/box/serialNos/delete'
        const body = {
          inspTimingCd: this.inspTimingCd,
          manufacturerCompanyCd: this.manufacturerCompanyCd,
          partCd: this.partCd,
          lotNo: this.lotNo,
          inspCompanyCd: this.inspCompanyCd,
          seq: this.deleteTarget.seq,
          versionNo: this.deleteTarget.versionNo,
        }
        await API.post(url, body)
        this.setSuccessMessage(this.$t('MSG0014'))
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0004', { p0: this.partCd })))
      } finally {
        this.deleteTarget = { seq: null, serialNo: '', versionNo: null }
        this.onClickSearch()
        this.stopLoading()
      }
    },
    onAddItem(SerialNo) {
      this.items.push({
        seq: null,
        serialNo: SerialNo,
        registeredBy: '',
        originalIndex: null,
        error: '',
      })
    },
    setRowClass(item) {
      return item?.error ? 'cell-error' : ''
    },
  },
}
</script>
